import {
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import type { ExpenseRecordListItemDTO } from "../api/client";
import { formatExpenseAmount } from "../lib/expense-format";
import { ExpenseRecordStatusBadge } from "./ExpenseRecordStatusBadge";

type ExpenseRecordsTableProps = {
  isLoading: boolean;
  data: ExpenseRecordListItemDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (id: string) => void;
};

export function ExpenseRecordsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onPaginationChange,
  onSortingChange,
  onView,
}: ExpenseRecordsTableProps) {
  const columns: TanstackReactTable.ColumnDef<ExpenseRecordListItemDTO>[] = [
    createSortableColumn<ExpenseRecordListItemDTO>("number", "ເລກທີ", {
      size: 130,
      cell: ({ row }) => (
        <span className="font-medium text-sm">{row.original.number}</span>
      ),
    }),
    createSortableColumn<ExpenseRecordListItemDTO>("expenseDate", "ວັນທີ", {
      size: 110,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.expenseDate)}
        </span>
      ),
    }),
    createSortableColumn<ExpenseRecordListItemDTO>("categoryName", "ຫມວດ", {
      size: 160,
      cell: ({ row }) => <span>{row.original.categoryName}</span>,
    }),
    createSortableColumn<ExpenseRecordListItemDTO>("payeeName", "ຜູ້ຮັບເງິນ", {
      size: 180,
      cell: ({ row }) => <span>{row.original.payeeName}</span>,
    }),
    createSortableColumn<ExpenseRecordListItemDTO>("amount", "ຈຳນວນ", {
      size: 130,
      cell: ({ row }) => (
        <span className="font-semibold">
          {formatExpenseAmount(row.original.amount)}
        </span>
      ),
    }),
    createSortableColumn<ExpenseRecordListItemDTO>("status", "ສະຖານະ", {
      size: 130,
      cell: ({ row }) => (
        <ExpenseRecordStatusBadge status={row.original.status} />
      ),
    }),
    {
      id: "actions",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງ",
              icon: <EyeIcon className="size-4" />,
              onClick: () => onView(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<ExpenseRecordListItemDTO, unknown>
      data={data}
      columns={columns}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onPaginationChange={onPaginationChange}
      onSortingChange={onSortingChange}
    />
  );
}
